import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Lock, Trophy } from "lucide-react";

const TIER_STYLES = {
  bronze: { border: "border-[#C27B4A]", text: "text-[#C27B4A]", glow: "shadow-[0_0_12px_rgba(194,123,74,0.35)]" },
  silver: { border: "border-gray-300", text: "text-gray-300", glow: "shadow-[0_0_12px_rgba(220,220,220,0.3)]" },
  gold:   { border: "border-[#F4D03F]", text: "text-[#F4D03F]", glow: "shadow-[0_0_14px_rgba(244,208,63,0.45)]" },
  mythic: { border: "border-[#FF007F]", text: "text-[#FF007F]", glow: "shadow-[0_0_16px_rgba(255,0,127,0.5)]" },
};

export default function PredictionBadges({ userId }) {
  const [badges, setBadges] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const url = userId ? `/users/${userId}/badges` : "/me/badges";
    api.get(url).then((r) => {
      setBadges(r.data?.badges || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [userId]);

  if (!loaded) return null;
  const earnedCount = badges.filter((b) => b.earned).length;

  return (
    <div className="border border-[#232330] bg-[#101015] p-5" data-testid="prediction-badges">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5 text-[#F4D03F]" /> Prediction badges
        </div>
        <div className="text-[10px] font-mono tracking-widest text-cyan-400 uppercase font-bold" data-testid="badges-earned-count">
          {earnedCount} / {badges.length} earned
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {badges.map((b) => {
          const style = TIER_STYLES[b.tier] || TIER_STYLES.bronze;
          const pct = Math.min(100, Math.round(((b.progress || 0) / Math.max(1, b.target)) * 100));
          return (
            <div
              key={b.id}
              data-testid={`badge-${b.id}`}
              className={`relative border p-2.5 flex flex-col items-center text-center transition-all ${
                b.earned
                  ? `${style.border} bg-[#0a0a12] ${style.glow}`
                  : "border-[#232330] bg-[#07070c] opacity-60"
              }`}
              title={b.desc}
            >
              {!b.earned && (
                <Lock className="w-3 h-3 text-gray-600 absolute top-1.5 right-1.5" />
              )}
              <div className={`font-heading font-black text-[11px] uppercase tracking-tight leading-tight mt-0.5 ${b.earned ? style.text : "text-gray-500"}`}>
                {b.label}
              </div>
              <div className="text-[9px] font-mono text-gray-500 mt-1 leading-tight">{b.desc}</div>
              {!b.earned && b.target > 1 && (
                <div className="w-full mt-2">
                  <div className="h-1 bg-[#232330] overflow-hidden">
                    <div className={`h-full ${style.text.replace("text-", "bg-")}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[9px] font-mono text-gray-500 mt-1">{b.progress}/{b.target}</div>
                </div>
              )}
              {b.earned && (
                <div className={`text-[9px] font-mono mt-1.5 font-bold uppercase tracking-widest ${style.text}`}>
                  ✓ Earned
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
